import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBootReady } from '@/lib/bootReady';
import { verifyLoginCode } from '@/lib/mfa';
import { supabase } from '@/lib/supabase';

/** Вход общей учётной записью UNIA: email + пароль. */
export function LoginPage() {
  const { user, login, logout, isLoading, mfaPending } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');

  // Заставка гаснет, как только форма готова: грузить здесь больше нечего.
  useBootReady(!isLoading);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { error: err } = await login(email.trim(), password);
      if (err) setError('Неверная почта или пароль');
    } catch {
      setError('Не получилось войти. Проверьте связь и попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  const field =
    'w-full min-h-12 px-4 rounded-xl bg-surface border border-line2 text-fg placeholder:text-faint';

  // Вход по коду: пароль принят, у человека подключён код — спрашиваем шесть цифр.
  const submitCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const { error: err } = await verifyLoginCode(supabase, code);
    setBusy(false);
    if (err) setError(err);
    // Успех: сессия стала aal2, AuthProvider сам пустит внутрь.
  };

  if (mfaPending) {
    return (
      <div className="h-full flex items-center justify-center p-6 overflow-y-auto">
        <div className="card w-full max-w-sm p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-fg">Код входа</h1>
            <p className="text-muted text-sm mt-2">
              Для вашей учётной записи включён вход по коду. Введите шесть цифр из приложения-аутентификатора.
            </p>
          </div>
          <form onSubmit={submitCode} className="space-y-4">
            <input
              id="mfa-code" type="text" required autoFocus autoComplete="one-time-code" inputMode="numeric"
              maxLength={7} value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="123456" aria-label="Код из приложения"
              className={`${field} text-center text-2xl tracking-widest`}
            />
            {error && <p role="alert" className="text-danger text-sm text-center">{error}</p>}
            <button
              type="submit" disabled={busy}
              className="w-full min-h-12 rounded-xl font-semibold bg-accent text-accent-ink disabled:opacity-50"
            >
              {busy ? 'Проверяем…' : 'Подтвердить'}
            </button>
            <button
              type="button" onClick={() => { setCode(''); setError(''); void logout(); }}
              className="w-full min-h-12 rounded-xl text-muted"
            >
              Войти другой учётной записью
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex items-center justify-center p-6 overflow-y-auto">
      <div className="card w-full max-w-sm p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-fg">
            Sale<span className="text-accent">us</span>
          </h1>
          <p className="text-muted text-sm mt-2">Войдите, чтобы продолжить</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-muted mb-1">Почта</label>
            <input
              id="email" type="email" required autoComplete="email" inputMode="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@unia.one" className={field}
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-muted mb-1">Пароль</label>
            <input
              id="password" type="password" required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className={field}
            />
          </div>
          {error && <p role="alert" className="text-danger text-sm text-center">{error}</p>}
          <button
            type="submit" disabled={busy}
            className="w-full min-h-12 rounded-xl font-semibold bg-accent text-accent-ink disabled:opacity-50"
          >
            {busy ? 'Входим…' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
}
